<template>
    <v-dialog
        :value="convertLeadModal"
        fullscreen
        hide-overlay
        persistent
        transition="dialog-bottom-transition"
        :content-class="twilioChat ? 'twilioDrawer' : ''"
    >
        <v-card>
            <v-card-title class="text-h5" primary-title>
                <v-layout>
                    <v-flex>Convert Lead to Reservation</v-flex>
                </v-layout>
            </v-card-title>
            <v-divider/>
            <v-card-text>
                <v-stepper v-model="currentStep">
                    <v-stepper-header>
                        <v-stepper-step
                            :color="currentStep === 1 ? 'secondary' : 'success'"
                            :complete="currentStep > 1"
                            step="1"
                            :class="currentStep === 1 ? 'title' : 'subheading'"
                        >
                            Reservation Options
                        </v-stepper-step>
                        <v-divider :class="currentStep > 1 ? 'success' : null" />
                        <v-stepper-step
                            :color="currentStep <= 2 ? 'secondary' : 'success'"
                            :complete="currentStep > 2"
                            step="2"
                            :class="currentStep === 2 ? 'title' : 'subheading'"
                        >
                            Select Unit
                        </v-stepper-step>
                        <v-divider :class="currentStep > 2 ? 'success' : null" />
                        <v-stepper-step
                            :color="currentStep <= 3 ? 'secondary' : 'success'"
                            :complete="currentStep > 3"
                            step="3"
                            :class="currentStep === 3 ? 'title' : 'subheading'"
                        >
                            Conversion Review
                        </v-stepper-step>
                        <v-divider :class="currentStep > 3 ? 'success' : null" />
                        <v-stepper-step
                            color="success"
                            :complete="currentStep === 4"
                            step="4"
                            :class="currentStep === 4 ? 'title' : 'subheading'"
                        >
                            Conversion Success
                        </v-stepper-step>
                    </v-stepper-header>
                    <v-stepper-items>
                        <v-stepper-content step="1">
                            <conversion-options />
                        </v-stepper-content>
                        <v-stepper-content step="2">
                            <unit-select
                                :lead="lead"
                                :unitFeatures="unitFeatures"
                                :availableUnits="availableUnits"
                                :infoLoading="infoLoading"
                                :convertedReservation="convertedReservation"
                                @setUnit="setUnit($event)"
                                @toOptions="toOptions"
                            />
                        </v-stepper-content>
                        <v-stepper-content step="3">
                            <conversion-review
                                :currentStep="currentStep"
                                :unitFeatures="unitFeatures"
                                :convertedReservation="convertedReservation"
                                :lead="lead"
                                @toOptions="toOptions"
                                @toUnitSelect="toUnitSelect"
                            />
                        </v-stepper-content>
                        <v-stepper-content step="4">
                            <conversion-success
                                :currentStep="currentStep"
                                :unitFeatures="unitFeatures"
                                :convertedReservation="convertedReservation"
                                :waitingListId="reserveUnitPayload.waiting_list_id"
                                :lead="lead"
                                @convertToRental="convertToRental"
                                @collapse="collapse"
                            />
                        </v-stepper-content>
                    </v-stepper-items>
                </v-stepper>
            </v-card-text>
            <v-divider />
            <v-card-actions>
                <v-layout v-if="currentStep === 4">
                    <v-btn
                        data-testid="convert-dismiss-btn"
                        class="convert-dismiss-btn mt-2 ml-3"
                        color="error"
                        @click="reservationSuccess"
                        large
                    >
                        Dismiss
                    </v-btn>
                    <v-spacer />
                    <disposition
                      :dispositionOpen="dispositionOpen"
                      @collapse="dispositionOpen = false"
                    >
                        <v-btn
                            data-testid="convert-disposition-btn"
                            class="convert-disposition-btn mt-2 mr-3"
                            color="primary"
                            @click.stop="dispositionOpen = true"
                            large
                        >
                            Disposition
                        </v-btn>
                    </disposition>
                </v-layout>
                <v-layout v-else>
                    <v-btn
                        data-testid="convert-close-btn"
                        class="convert-close-btn error mt-2 ml-3"
                        @click="collapse"
                        large
                    >
                        Close
                    </v-btn>
                    <v-spacer />
                    <v-btn
                        data-testid="convert-select-unit-btn"
                        class="convert-select-unit-btn primary mt-2 mr-3"
                        v-if="currentStep === 1"
                        :disabled="!validLeadInformation"
                        @click="toUnitSelect"
                        large
                    >
                        Select Unit
                    </v-btn>
                    <v-btn
                        data-testid="convert-review-conversion-btn"
                        class="convert-review-conversion-btn primary mt-2 mr-3"
                        v-if="currentStep === 2"
                        :loading="reserveUnitLoading"
                        :disabled="!convertedReservation"
                        @click="toSummary"
                        large
                    >
                        Review Conversion
                    </v-btn>
                    <v-btn
                        data-testid="convert-submit-update-btn"
                        class="convert-submit-update-btn success mt-2 mr-3"
                        v-if="currentStep === 3"
                        :loading="reserveUnitLoading"
                        @click="updateReservation"
                        large
                    >
                        Submit
                    </v-btn>
                </v-layout>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { inputRules, updateEmailRules, updatePhoneRules } from "@/helpers/form-rules";
import ConversionOptions from "@/components/convert-lead/steps/ConversionOptions";
import ConversionReview from "@/components/convert-lead/steps/ConversionReview";
import ConversionSuccess from "@/components/convert-lead/steps/ConversionSuccess";
import Disposition from "@/components/Disposition";
import UnitSelect from "@/components/convert-lead/partials/UnitSelect";
import { mapState } from "vuex";
import moment from "moment";

export default {
  props: ["lead", "convertLeadModal", "unitFeatures"],

  data() {
    return {
      loading: false,
      reserveUnitLoading: false,
      paymentLoading: false,
      infoLoading: false,
      noteLoading: false,

      visible: false,
      dispositionOpen: false,
      cancelDisabled: false,

      reservationType: "",

      moveInSelect: null,
      followUpSelect: null,
      tenant_id: null,
      waiting_list_id: null,
      note: "",

      convertedReservation: "",
      availableUnits: [],

      currentStep: 1,

      inputRules: inputRules,
      emailRules: updateEmailRules,
      phoneRules: updatePhoneRules
    };
  },

  methods: {
    getUnits() {
      this.infoLoading = true;
      this.$store.dispatch("getFacility", this.$route.params.facilityId)
        .then(resp => {
          const facility = resp.data.facility;
          this.availableUnits = facility.units.parking
            ? facility.units.storage.concat(facility.units.parking)
            : facility.units.storage;
          this.infoLoading = false;
        })
        .catch(err => {
          this.availableUnits = [];
          this.$store.dispatch("handleError", err);
          this.infoLoading = false;
        });
    },

    setUnit(unit) {
      this.convertedReservation = unit;
    },

    toOptions() {
      this.currentStep = 1;
    },

    toUnitSelect() {
      this.currentStep = 2;
      this.getUnits();
    },

    toSummary() {
      this.currentStep = 3;
    },

    toConfirmation() {
      this.currentStep = 4;
    },

    collapse() {
      this.$emit("collapse");
      this.convertedReservation = "";
      this.toOptions();
    },

    reservationSuccess() {
      this.$emit("reservationCreated");
      this.collapse();
    },

    updateReservation() {
      this.reserveUnitLoading = true;
      this.$store.dispatch("updateReservation", this.reserveUnitPayload)
        .then(resp => {
          this.$store.commit("setReservationData", this.reserveUnitPayload);
          if (resp.status === 200) this.$store.commit("setReservationId", resp.data.reservation_id);
          this.$store.dispatch("handleSuccess", "Reservation successfully created!");
          this.toConfirmation();
          this.reserveUnitLoading = false;
        })
        .catch(err => {
          this.$store.dispatch("handleError", err);
          this.reserveUnitLoading = false;
        });
    },

    convertToRental(unitInfo) {
      // we combine this.lead with unit info so that tenant data transfers when converting to rental.
      this.$emit("convertToRental", Object.assign({}, this.lead, unitInfo));
    }
  },

  computed: {
    ...mapState({
      tenant: state => state.tenant.info,
      options: state => state.tenant.options,
      darkMode: state => state.application.settings.darkTheme,
      twilioChat: state => state.application.twilioChat
    }),

    today() {
      let now = new Date();
      return new moment(now).format("YYYY-MM-DD");
    },

    threeWeeks() {
      let date = new Date();
      return new moment(date).add(21, "days").format("YYYY-MM-DD");
    },

    validLeadInformation() {
      let regex = /^\S+@\S+\.\S+$/;
      return (
        this.tenant.first_name &&
                this.tenant.last_name &&
                regex.test(this.tenant.email) &&
                (this.tenant.phone_number && this.tenant.phone_number.length) === 12 &&
                this.options.moveInDate &&
                this.options.followupDate
      );
    },

    reserveUnitPayload() {
      return {
        first_name: this.tenant.first_name,
        last_name: this.tenant.last_name,
        email: this.tenant.email,
        phone: this.tenant.phone_number,
        tenant_id: parseFloat(this.lead.tenant_id),
        move_in_date: this.options.moveInDate,
        facilityId: this.$route.params.facilityId,
        waitingListId: this.lead.waiting_list_id,
        unit_id: +this.convertedReservation.unit_id_first_available,
        unit_dimensions: this.convertedReservation.width + " x " + this.convertedReservation.length,
        promotion_id: this.convertedReservation.concession_id,
        promotion_text: this.convertedReservation.promotion_text,
        unit_type: this.convertedReservation.product_type,
        quoted_rate: +parseFloat(this.convertedReservation.rate).toFixed(2),
        reservation_type: 2,
        status: 0,
        inquiry_type: 3,
        follow_up_date: moment(this.options.followupDate).format("MM/DD/YYYY"),
        is_follow_up: true,
        customer_source: localStorage.getItem("customerSource")
      };
    }
  },

  components: {
    Disposition,
    UnitSelect,
    ConversionOptions,
    ConversionReview,
    ConversionSuccess
  }
};
</script>
